import { useState, useEffect } from 'react';
import {
    Plus,
    Edit01,
    Trash01,
    Save01
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Tabs } from '@/components/application/tabs/tabs';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { LanguageSelector } from '@/components/application/language-selector/language-selector';
import { ThemeSwitcher } from '@/components/application/theme-switcher/theme-switcher';
import { apiKeyService, serviceConfigService } from '@/data/mock-settings';
import { accountService } from '@/data/mock-accounts';
import type { ApiKey, ServiceConfig, ServiceCategory } from '@/types/settings';
import type { EntityType } from '@/types/account';

interface ApiKeyFormData {
    name: string;
    service: string;
    category: ServiceCategory;
    type: string;
    value: string;
    description: string;
    isActive: boolean;
}

interface ServiceConfigFormData {
    name: string;
    category: ServiceCategory;
    baseUrl: string;
    isEnabled: boolean;
    config: string;
}

interface ProfileFormData {
    legalName: string;
    email: string;
    phone: string;
    address: {
        street: string;
        city: string;
        state: string;
        postalCode: string;
    };
    country: string;
    entityType: EntityType | '';
}

export const SharedSettingsPage = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [serviceConfigs, setServiceConfigs] = useState<ServiceConfig[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<ServiceCategory | ''>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState<'api-key' | 'service-config'>('api-key');
    const [editingItem, setEditingItem] = useState<ApiKey | ServiceConfig | null>(null);

    // Profile form state
    const [profileForm, setProfileForm] = useState<ProfileFormData>({
        legalName: '',
        email: '',
        phone: '',
        address: {
            street: '',
            city: '',
            state: '',
            postalCode: ''
        },
        country: '',
        entityType: ''
    });

    const [apiKeyForm, setApiKeyForm] = useState<ApiKeyFormData>({
        name: '',
        service: '',
        category: 'AI/ML',
        type: 'API Key',
        value: '',
        description: '',
        isActive: true
    });

    const [serviceConfigForm, setServiceConfigForm] = useState<ServiceConfigFormData>({
        name: '',
        category: 'AI/ML',
        baseUrl: '',
        isEnabled: true,
        config: ''
    });

    const entityTypeOptions = [
        { id: 'LLC', label: 'LLC' },
        { id: 'Corporation', label: 'Corporation' },
        { id: 'Partnership', label: 'Partnership' },
        { id: 'Sole Proprietorship', label: 'Sole Proprietorship' },
        { id: 'NGO', label: 'NGO' }
    ];

    const categoryOptions = [
        { id: '', label: 'Tutte le categorie' },
        { id: 'AI/ML', label: 'AI/ML' },
        { id: 'Payment', label: 'Payment' },
        { id: 'Email', label: 'Email' },
        { id: 'Cloud', label: 'Cloud' },
        { id: 'Analytics', label: 'Analytics' },
        { id: 'Other', label: 'Other' }
    ];

    const typeOptions = [
        { id: 'API Key', label: 'API Key' },
        { id: 'Secret Key', label: 'Secret Key' },
        { id: 'Token', label: 'Token' },
        { id: 'Password', label: 'Password' },
        { id: 'Certificate', label: 'Certificate' }
    ];

    const countryOptions = [
        { id: 'United States', label: 'United States' },
        { id: 'United Kingdom', label: 'United Kingdom' },
        { id: 'Germany', label: 'Germany' },
        { id: 'France', label: 'France' },
        { id: 'Italy', label: 'Italy' },
        { id: 'Spain', label: 'Spain' },
        { id: 'Netherlands', label: 'Netherlands' },
        { id: 'Switzerland', label: 'Switzerland' },
        { id: 'Canada', label: 'Canada' }
    ];

    // Load account data and settings
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current account (mock - in real app would come from auth context)
            const accountId = localStorage.getItem('accountId') || '1';
            const currentAccount = accountService.getById(accountId);

            if (currentAccount) {
                setProfileForm({
                    legalName: currentAccount.legalName,
                    email: currentAccount.email,
                    phone: currentAccount.phone || '',
                    address: {
                        street: currentAccount.address.street,
                        city: currentAccount.address.city,
                        state: currentAccount.address.state,
                        postalCode: currentAccount.address.postalCode
                    },
                    country: currentAccount.country,
                    entityType: currentAccount.entityType
                });
            }

            const apiKeysData = apiKeyService.getAll();
            const serviceConfigsData = serviceConfigService.getAll();

            setApiKeys(apiKeysData);
            setServiceConfigs(serviceConfigsData);
            setIsLoading(false);
        };

        loadData();
    }, []);

    const handleSaveProfile = async () => {
        setIsSaving(true);
        setSaveSuccess(false);

        try {
            // Validation
            if (!profileForm.legalName || !profileForm.email || !profileForm.country) {
                alert('Compila tutti i campi obbligatori');
                setIsSaving(false);
                return;
            }

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Update account (mock - in real app would call API)
            const accountId = localStorage.getItem('accountId') || '1';
            accountService.update(accountId, {
                id: accountId,
                legalName: profileForm.legalName,
                email: profileForm.email,
                phone: profileForm.phone,
                address: profileForm.address,
                country: profileForm.country,
                entityType: profileForm.entityType as EntityType
            });

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            alert('Errore durante il salvataggio. Riprova.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenModal = (type: 'api-key' | 'service-config', item?: ApiKey | ServiceConfig) => {
        setModalType(type);
        setEditingItem(item || null);

        if (item) {
            if (type === 'api-key') {
                const apiKey = item as ApiKey;
                setApiKeyForm({
                    name: apiKey.name,
                    service: apiKey.service,
                    category: apiKey.category,
                    type: apiKey.type,
                    value: apiKey.value,
                    description: apiKey.description || '',
                    isActive: apiKey.isActive
                });
            } else {
                const serviceConfig = item as ServiceConfig;
                setServiceConfigForm({
                    name: serviceConfig.name,
                    category: serviceConfig.category,
                    baseUrl: serviceConfig.baseUrl || '',
                    isEnabled: serviceConfig.isEnabled,
                    config: JSON.stringify(serviceConfig.config, null, 2)
                });
            }
        } else {
            // Reset forms for new items
            setApiKeyForm({
                name: '',
                service: '',
                category: 'AI/ML',
                type: 'API Key',
                value: '',
                description: '',
                isActive: true
            });
            setServiceConfigForm({
                name: '',
                category: 'AI/ML',
                baseUrl: '',
                isEnabled: true,
                config: ''
            });
        }

        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSaveApiKey = () => {
        if (editingItem) {
            apiKeyService.update(editingItem.id, { ...apiKeyForm, id: editingItem.id, type: apiKeyForm.type as any });
        } else {
            apiKeyService.create({ ...apiKeyForm, type: apiKeyForm.type as any });
        }

        setApiKeys(apiKeyService.getAll());
        handleCloseModal();
    };

    const handleSaveServiceConfig = () => {
        try {
            const config = JSON.parse(serviceConfigForm.config || '{}');
            if (editingItem) {
                serviceConfigService.update(editingItem.id, {
                    ...serviceConfigForm,
                    id: editingItem.id,
                    config
                });
            } else {
                serviceConfigService.create({
                    ...serviceConfigForm,
                    config
                });
            }

            setServiceConfigs(serviceConfigService.getAll());
            handleCloseModal();
        } catch (error) {
            alert('Errore nel formato JSON. Inserisci un JSON valido.');
        }
    };

    const handleDelete = (id: string, type: 'api-key' | 'service-config') => {
        if (confirm('Sei sicuro di voler eliminare questo elemento?')) {
            if (type === 'api-key') {
                apiKeyService.delete(id);
                setApiKeys(apiKeyService.getAll());
            } else {
                serviceConfigService.delete(id);
                setServiceConfigs(serviceConfigService.getAll());
            }
        }
    };

    const getStatusBadge = (isActive: boolean) => {
        return isActive ? (
            <Badge color="success" size="sm">Attivo</Badge>
        ) : (
            <Badge color="error" size="sm">Inattivo</Badge>
        );
    };

    const getCategoryBadge = (category: ServiceCategory) => {
        const colors = {
            'AI/ML': 'brand',
            'Payment': 'success',
            'Email': 'blue',
            'Cloud': 'purple',
            'Analytics': 'orange',
            'Other': 'gray'
        } as const;

        return (
            <Badge color={colors[category] || 'gray'} size="sm">
                {category}
            </Badge>
        );
    };

    // Filter API keys and service configs
    const filteredApiKeys = apiKeys.filter(key => {
        const matchesSearch = !searchTerm ||
            key.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            key.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
            key.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !categoryFilter || key.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    // Temporary use of serviceConfigs to avoid unused variable error
    console.log('Service configs loaded:', serviceConfigs.length);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">Caricamento impostazioni...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">Impostazioni</h1>
                    <p className="text-tertiary mt-1">
                        Gestisci le impostazioni del tuo account
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as string)}>
                <Tabs.List items={[
                    { id: "profile", label: "Profilo" },
                    { id: "api-keys", label: "API Keys", badge: apiKeys.length },
                    { id: "preferences", label: "Preferenze" }
                ]}>
                    <Tabs.Item id="profile" label="Profilo" />
                    <Tabs.Item id="api-keys" label="API Keys" badge={apiKeys.length} />
                    <Tabs.Item id="preferences" label="Preferenze" />
                </Tabs.List>

                {/* Profile Tab */}
                <Tabs.Panel id="profile">
                    <div className="space-y-6">
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-6">Informazioni Account</h2>

                            <div className="space-y-6">
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Input
                                        label="Nome Legale"
                                        placeholder="Inserisci il nome legale"
                                        value={profileForm.legalName}
                                        onChange={(value) => setProfileForm({ ...profileForm, legalName: value })}
                                        isRequired
                                        size="md"
                                    />
                                    <Input
                                        label="Email"
                                        placeholder="email@esempio.com"
                                        value={profileForm.email}
                                        onChange={(value) => setProfileForm({ ...profileForm, email: value })}
                                        isRequired
                                        size="md"
                                        type="email"
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Input
                                        label="Telefono"
                                        placeholder="+39 123 456 7890"
                                        value={profileForm.phone}
                                        onChange={(value) => setProfileForm({ ...profileForm, phone: value })}
                                        size="md"
                                    />
                                    <Select
                                        label="Tipo Entità"
                                        placeholder="Seleziona il tipo di entità"
                                        items={entityTypeOptions}
                                        selectedKey={profileForm.entityType}
                                        onSelectionChange={(key) => setProfileForm({ ...profileForm, entityType: key as EntityType | '' })}
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>

                                <Input
                                    label="Indirizzo"
                                    placeholder="Via, numero civico"
                                    value={profileForm.address.street}
                                    onChange={(value) => setProfileForm({
                                        ...profileForm,
                                        address: { ...profileForm.address, street: value }
                                    })}
                                    size="md"
                                />

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Input
                                        label="Città"
                                        placeholder="Città"
                                        value={profileForm.address.city}
                                        onChange={(value) => setProfileForm({
                                            ...profileForm,
                                            address: { ...profileForm.address, city: value }
                                        })}
                                        size="md"
                                    />
                                    <Input
                                        label="Provincia/Stato"
                                        placeholder="Provincia o Stato"
                                        value={profileForm.address.state}
                                        onChange={(value) => setProfileForm({
                                            ...profileForm,
                                            address: { ...profileForm.address, state: value }
                                        })}
                                        size="md"
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Input
                                        label="CAP"
                                        placeholder="Codice postale"
                                        value={profileForm.address.postalCode}
                                        onChange={(value) => setProfileForm({
                                            ...profileForm,
                                            address: { ...profileForm.address, postalCode: value }
                                        })}
                                        size="md"
                                    />
                                    <Select
                                        label="Paese"
                                        placeholder="Seleziona il paese"
                                        items={countryOptions}
                                        selectedKey={profileForm.country}
                                        onSelectionChange={(key) => setProfileForm({ ...profileForm, country: key as string })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>

                                <div className="flex items-center justify-end gap-4 pt-4 border-t border-secondary">
                                    {saveSuccess && (
                                        <span className="text-sm text-success-600">Modifiche salvate con successo!</span>
                                    )}
                                    <Button
                                        onClick={handleSaveProfile}
                                        size="md"
                                        color="primary"
                                        iconLeading={Save01}
                                        isDisabled={isSaving}
                                    >
                                        {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Tabs.Panel>

                {/* API Keys Tab */}
                <Tabs.Panel id="api-keys">
                    <div className="space-y-6">
                        {/* Filters */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <Input
                                    placeholder="Cerca per nome, servizio..."
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    size="md"
                                />
                                <Select
                                    placeholder="Filtra per categoria"
                                    items={categoryOptions}
                                    selectedKey={categoryFilter}
                                    onSelectionChange={(key) => setCategoryFilter(key as ServiceCategory | '')}
                                    size="md"
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>
                                <Button
                                    onClick={() => handleOpenModal('api-key')}
                                    size="md"
                                    color="primary"
                                    iconLeading={Plus}
                                    className="w-full sm:w-auto"
                                >
                                    Nuova API Key
                                </Button>
                            </div>
                        </div>

                        {/* API Keys Table */}
                        <TableCard.Root>
                            <TableCard.Header
                                title={`API Keys (${filteredApiKeys.length})`}
                                description="Gestisci le chiavi API per i servizi esterni"
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label="Nome" isRowHeader />
                                    <Table.Head label="Servizio" />
                                    <Table.Head label="Categoria" />
                                    <Table.Head label="Tipo" />
                                    <Table.Head label="Stato" />
                                    <Table.Head label="Azioni" />
                                </Table.Header>
                                <Table.Body>
                                    {filteredApiKeys.length === 0 ? (
                                        <Table.Row>
                                            <Table.Cell colSpan={6} className="text-center py-8">
                                                <p className="text-tertiary">Nessuna API Key trovata</p>
                                            </Table.Cell>
                                        </Table.Row>
                                    ) : (
                                        filteredApiKeys.map((key) => (
                                            <Table.Row key={key.id}>
                                                <Table.Cell>
                                                    <div>
                                                        <div className="font-medium text-primary">{key.name}</div>
                                                        <div className="text-sm text-tertiary">{key.description}</div>
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <span className="text-sm text-tertiary">{key.service}</span>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {getCategoryBadge(key.category)}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <span className="text-sm text-tertiary">{key.type}</span>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {getStatusBadge(key.isActive)}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            onClick={() => handleOpenModal('api-key', key)}
                                                            size="sm"
                                                            color="secondary"
                                                            iconLeading={Edit01}
                                                        >
                                                            Modifica
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleDelete(key.id, 'api-key')}
                                                            size="sm"
                                                            color="secondary-destructive"
                                                            iconLeading={Trash01}
                                                        >
                                                            Elimina
                                                        </Button>
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))
                                    )}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    </div>
                </Tabs.Panel>

                {/* Preferences Tab */}
                <Tabs.Panel id="preferences">
                    <div className="space-y-6">
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-6">Preferenze</h2>
                            <div className="space-y-6">
                                <LanguageSelector />
                                <ThemeSwitcher />
                            </div>
                        </div>
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Modal for API Keys and Service Configs */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingItem ? 'Modifica' : 'Nuovo'} {modalType === 'api-key' ? 'API Key' : 'Servizio'}
                                </h2>

                                {modalType === 'api-key' ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Input
                                                label="Nome"
                                                placeholder="Nome della chiave"
                                                value={apiKeyForm.name}
                                                onChange={(value) => setApiKeyForm({ ...apiKeyForm, name: value })}
                                                isRequired
                                                size="md"
                                            />
                                            <Input
                                                label="Servizio"
                                                placeholder="Nome del servizio"
                                                value={apiKeyForm.service}
                                                onChange={(value) => setApiKeyForm({ ...apiKeyForm, service: value })}
                                                isRequired
                                                size="md"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Select
                                                label="Categoria"
                                                items={categoryOptions.filter(opt => opt.id !== '')}
                                                selectedKey={apiKeyForm.category}
                                                onSelectionChange={(key) => setApiKeyForm({ ...apiKeyForm, category: key as ServiceCategory })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                            <Select
                                                label="Tipo"
                                                items={typeOptions}
                                                selectedKey={apiKeyForm.type}
                                                onSelectionChange={(key) => setApiKeyForm({ ...apiKeyForm, type: key as string })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>

                                        <Input
                                            label="Valore"
                                            placeholder="Inserisci il valore della chiave"
                                            value={apiKeyForm.value}
                                            onChange={(value) => setApiKeyForm({ ...apiKeyForm, value: value })}
                                            isRequired
                                            size="md"
                                            type="password"
                                        />

                                        <Input
                                            label="Descrizione"
                                            placeholder="Descrizione opzionale"
                                            value={apiKeyForm.description}
                                            onChange={(value) => setApiKeyForm({ ...apiKeyForm, description: value })}
                                            size="md"
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Input
                                                label="Nome"
                                                placeholder="Nome del servizio"
                                                value={serviceConfigForm.name}
                                                onChange={(value) => setServiceConfigForm({ ...serviceConfigForm, name: value })}
                                                isRequired
                                                size="md"
                                            />
                                            <Select
                                                label="Categoria"
                                                items={categoryOptions.filter(opt => opt.id !== '')}
                                                selectedKey={serviceConfigForm.category}
                                                onSelectionChange={(key) => setServiceConfigForm({ ...serviceConfigForm, category: key as ServiceCategory })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>

                                        <Input
                                            label="Base URL"
                                            placeholder="https://api.example.com"
                                            value={serviceConfigForm.baseUrl}
                                            onChange={(value) => setServiceConfigForm({ ...serviceConfigForm, baseUrl: value })}
                                            size="md"
                                        />

                                        <Input
                                            label="Configurazione JSON"
                                            placeholder='{"key": "value"}'
                                            value={serviceConfigForm.config}
                                            onChange={(value) => setServiceConfigForm({ ...serviceConfigForm, config: value })}
                                            size="md"
                                        />
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={handleCloseModal}
                                        size="md"
                                        color="secondary"
                                    >
                                        Annulla
                                    </Button>
                                    <Button
                                        onClick={modalType === 'api-key' ? handleSaveApiKey : handleSaveServiceConfig}
                                        size="md"
                                        color="primary"
                                    >
                                        {editingItem ? 'Salva Modifiche' : 'Crea'}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};

